import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { withCustomStatuses } from '../../utils/orderStatuses.js';
import './StatusSelect.css';

const CUSTOM_SENTINEL = '__custom_status__';

// Status picker that can define a new status without leaving the form.
// Unlike FreightDestinationSelect's one-off custom destination, a status typed
// here is saved to the account's list (via onAddStatus) — it has to be a real,
// reusable status so the Procurement Tracker can filter on it and schedule
// items imported from the PO can keep it.
export default function StatusSelect({
  value,
  builtInStatuses,
  customStatuses = [],
  onChange,
  onAddStatus,
  disabled = false,
  className = '',
}) {
  const [showModal, setShowModal] = useState(false);
  const [label, setLabel] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const options = withCustomStatuses(builtInStatuses, customStatuses, value);
  const canAdd = Boolean(onAddStatus) && !disabled;

  function handleChange(e) {
    const next = e.target.value;
    if (next === CUSTOM_SENTINEL) {
      setLabel('');
      setError(null);
      setShowModal(true);
      return;
    }
    onChange(next);
  }

  function closeModal() {
    setShowModal(false);
    setLabel('');
    setError(null);
  }

  async function saveCustom() {
    const trimmed = label.trim();
    if (!trimmed) return;

    // Already a known status — just select it rather than saving a duplicate.
    const existing = options.find((s) => s.toLowerCase() === trimmed.toLowerCase());
    if (existing) {
      onChange(existing);
      closeModal();
      return;
    }

    setSaving(true);
    setError(null);
    try {
      await onAddStatus(trimmed);
      onChange(trimmed);
      closeModal();
    } catch (err) {
      console.error('Failed to add custom status:', err);
      setError(err?.message || 'Could not save that status. Try again.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <select className={className} value={value || ''} disabled={disabled} onChange={handleChange}>
        {options.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
        {canAdd && <option value={CUSTOM_SENTINEL}>+ Custom status…</option>}
      </select>

      {/* Portalled to the body: the Item Status modal this can open from sits
          inside a .modal-card, whose `will-change: transform` would otherwise
          become the containing block for a position:fixed overlay (and clip it
          against the card's own scroll area). React events still bubble
          through the component tree, so the parent modal's stopPropagation
          keeps a click in here from closing it. */}
      {showModal && createPortal(
        <div className="status-select-modal-overlay" onClick={closeModal}>
          <div className="status-select-modal" onClick={(e) => e.stopPropagation()}>
            <h4>Custom Status</h4>
            <p>
              Saved to your account, so you can reuse it on other purchase orders and schedule
              items, and filter by it on the Procurement Tracker.
            </p>

            <label className="status-select-modal-label">Status Name</label>
            <input
              autoFocus
              type="text"
              placeholder="e.g. Awaiting CFA"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Escape') closeModal();
                if (e.key === 'Enter') {
                  e.preventDefault();
                  saveCustom();
                }
              }}
            />
            <p className="status-select-modal-hint">
              Manage the full list under Settings &rsaquo; Sales Account &rsaquo; Order Statuses.
            </p>

            {error && <p className="status-select-modal-error">{error}</p>}

            <div className="status-select-modal-actions">
              <button type="button" className="status-select-cancel-btn" onClick={closeModal}>
                Cancel
              </button>
              <button
                type="button"
                className="status-select-save-btn"
                onClick={saveCustom}
                disabled={saving || !label.trim()}
              >
                {saving ? 'Saving…' : 'Add Status'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
}
